import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { playNewMessage, playRequestAccepted, playSuccess, playTabSwitch } from '../utils/SoundManager';
import { FloatingHearts } from '../components/VisualEffects';
import { VerifiedBadge } from '../components/VerifiedBadge';
import UpgradeModal from '../components/UpgradeModal';
import HeaderNotifications from '../components/HeaderNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// دالة الوقت النسبي بالعربي
function getRelativeTime(dateStr) {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);

    if (diffSec < 60) return 'الآن';
    if (diffMin === 1) return 'منذ دقيقة';
    if (diffMin === 2) return 'منذ دقيقتين';
    if (diffMin <= 10) return `منذ ${diffMin} دقائق`;
    if (diffMin < 60) return `منذ ${diffMin} دقيقة`;
    if (diffHour === 1) return 'منذ ساعة';
    if (diffHour === 2) return 'منذ ساعتين';
    if (diffHour <= 10) return `منذ ${diffHour} ساعات`;
    if (diffHour < 24) return `منذ ${diffHour} ساعة`;
    if (diffDay === 1) return 'منذ يوم';
    if (diffDay === 2) return 'منذ يومين';
    if (diffDay <= 10) return `منذ ${diffDay} أيام`;
    if (diffDay < 30) return `منذ ${diffDay} يوم`;
    if (diffWeek <= 4) return `منذ ${diffWeek} أسبوع`;
    if (diffMonth === 1) return 'منذ شهر';
    if (diffMonth === 2) return 'منذ شهرين';
    return `منذ ${diffMonth} أشهر`;
  } catch {
    return '';
  }
}

// أيقونة حالة الرسالة (ظرف)
function MessageStatusIcon({ conv, currentUserId }) {
  const lastMsg = conv.last_message;
  if (!lastMsg) return null;

  const isMySent = lastMsg.from_user_id === currentUserId;

  if (isMySent) {
    const isRead = conv.my_last_msg_read === true;
    if (isRead) {
      // علامتين زرقاء - تمت القراءة
      return (
        <div data-testid="msg-status-read" style={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: '2px'
        }}>
          <span style={{ 
            color: '#1E88E5', 
            fontSize: '18px', 
            fontWeight: '800',
            letterSpacing: '-3px'
          }}>✓✓</span>
        </div>
      );
    } else {
      // علامة واحدة رمادية - لم تُقرأ
      return (
        <div data-testid="msg-status-unread" style={{ 
          display: 'flex', 
          alignItems: 'center'
        }}>
          <span style={{ 
            color: '#9e9e9e', 
            fontSize: '18px', 
            fontWeight: '800'
          }}>✓</span>
        </div>
      );
    }
  } else {
    if (conv.unread_count > 0) {
      return null; // العداد يظهر بدل الأيقونة
    }
    return null;
  }
}

// مؤشر الاتصال (نقطة خضراء/رمادية)
function OnlineIndicator({ isOnline }) {
  return (
    <div
      data-testid={isOnline ? "online-indicator" : "offline-indicator"}
      style={{
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        background: isOnline ? '#4CAF50' : '#bdbdbd',
        border: '2px solid white',
        boxShadow: isOnline ? '0 0 6px rgba(76, 175, 80, 0.6)' : 'none',
        position: 'absolute',
        bottom: '0',
        left: '0'
      }}
    />
  );
}

function MessagesPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('conversations');
  const [conversations, setConversations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalMsg, setUpgradeModalMsg] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [showHearts, setShowHearts] = useState(false);
  const messagesEndRef = useRef(null);

  const getHeaders = () => {
    const userId = localStorage.getItem('userId');
    return userId ? { 'X-User-Id': userId } : {};
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // تحديث المحادثات كل 30 ثانية
  useEffect(() => {
    if (activeTab === 'conversations') {
      const interval = setInterval(() => {
        fetchData();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true, headers: getHeaders() });
      setCurrentUser(response.data);
    } catch (err) {
      navigate('/login');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'conversations') {
        const response = await axios.get(`${BACKEND_URL}/api/conversations`, { withCredentials: true, headers: getHeaders() });
        setConversations(response.data.conversations || []);
      } else if (activeTab === 'requests') {
        const response = await axios.get(`${BACKEND_URL}/api/connections/received`, { withCredentials: true });
        setRequests(response.data.requests || []);
      } else if (activeTab === 'sent') {
        const response = await axios.get(`${BACKEND_URL}/api/connections/sent`, { withCredentials: true });
        setSentRequests(response.data.requests || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (connectionId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/messages/${connectionId}`,
        { withCredentials: true }
      );
      setMessages(response.data.messages || []);
      
      await axios.post(
        `${BACKEND_URL}/api/messages/${connectionId}/read`,
        {},
        { withCredentials: true }
      );
      // تحديث عداد الرسائل غير المقروءة
      fetchData();
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    fetchMessages(conv.connection_id);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSendingMessage(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/messages/send`,
        {
          connection_request_id: selectedConversation.connection_id,
          content: newMessage
        },
        { withCredentials: true, headers: getHeaders() }
      );
      setNewMessage('');
      playSuccess();
      fetchMessages(selectedConversation.connection_id);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'فشل إرسال الرسالة';
      // اكتشاف نوع الخطأ: قيد العضوية المجانية
      const isLimitError = err.response?.status === 403 && 
        (errorMsg.includes('مجانية') || errorMsg.includes('اشترك') || errorMsg.includes('العضوية'));
      
      if (isLimitError) {
        setUpgradeModalMsg(errorMsg);
        setShowUpgradeModal(true);
      } else {
        alert(errorMsg);
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const handleResponse = async (requestId, status) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/connections/${requestId}/respond`,
        { status },
        { withCredentials: true, headers: getHeaders() }
      );
      
      if (status === 'accepted') {
        playRequestAccepted();
        setShowHearts(true);
      }
      
      fetchData();
      alert(status === 'accepted' ? 'تم قبول الطلب! يمكنك الآن التواصل' : 'تم رفض الطلب');
    } catch (err) {
      alert('حدث خطأ');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--light-bg)' }}>
      <FloatingHearts trigger={showHearts} onComplete={() => setShowHearts(false)} />
      
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <h1 className="logo" onClick={() => navigate('/dashboard')} style={{cursor: 'pointer'}}>وِداد</h1>
          <div className="nav-menu" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HeaderNotifications />
            <button className="nav-link" onClick={() => navigate('/dashboard')}>الرئيسية</button>
            <button className="nav-link" onClick={() => navigate('/search')}>البحث</button>
            <button className="nav-link" style={{background: 'var(--light-bg)', color: 'var(--primary)'}}>الرسائل</button>
            <button className="nav-link" onClick={() => navigate('/pricing')}>الباقات</button>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '20px' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <button
            data-testid="tab-conversations"
            className={`btn ${activeTab === 'conversations' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => { playTabSwitch(); setActiveTab('conversations'); setSelectedConversation(null); }}
          >
            المحادثات
          </button>
          <button
            data-testid="tab-requests"
            className={`btn ${activeTab === 'requests' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => { playTabSwitch(); setActiveTab('requests'); setSelectedConversation(null); }}
          >
            طلبات واردة
            {requests.length > 0 && (
              <span style={{ 
                background: 'var(--error)', 
                color: 'white', 
                borderRadius: '50%', 
                padding: '2px 8px', 
                fontSize: '12px',
                marginRight: '8px'
              }}>
                {requests.length}
              </span>
            )}
          </button>
          <button
            data-testid="tab-sent"
            className={`btn ${activeTab === 'sent' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => { playTabSwitch(); setActiveTab('sent'); setSelectedConversation(null); }}
          >
            طلبات مرسلة
          </button>
        </div>

        {loading && (
          <div className="loading">
            <div className="spinner"></div>
            <p>جاري التحميل...</p>
          </div>
        )}

        {/* Conversations Tab */}
        {!loading && activeTab === 'conversations' && (
          <div style={{ display: 'grid', gridTemplateColumns: selectedConversation ? '380px 1fr' : '1fr', gap: '20px' }}>
            {/* Conversations List */}
            <div>
              {conversations.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <div style={{ fontSize: '64px', marginBottom: '16px' }}>💬</div>
                  <h3 style={{ color: 'var(--text-dark)', marginBottom: '8px' }}>لا توجد محادثات</h3>
                  <p style={{ color: 'var(--text-light)' }}>ابدأ بالبحث والتواصل مع الآخرين</p>
                  <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={() => navigate('/search')}>
                    ابدأ البحث
                  </button>
                </div>
              ) : (
                <div className="card" style={{ padding: '0', overflow: 'hidden', borderRadius: '16px' }}>
                  {conversations.map((conv) => {
                    const isSelected = selectedConversation?.connection_id === conv.connection_id;
                    const hasUnread = conv.unread_count > 0;
                    const otherUser = conv.other_user;
                    const lastMsgTime = conv.last_message?.created_at;
                    const isMySent = conv.last_message?.from_user_id === currentUser?.user_id;
                    
                    return (
                      <div
                        key={conv.connection_id}
                        data-testid={`conversation-item-${conv.connection_id}`}
                        onClick={() => selectConversation(conv)}
                        style={{
                          padding: '14px 18px',
                          borderBottom: '1px solid var(--border-color)',
                          cursor: 'pointer',
                          background: isSelected 
                            ? 'var(--light-bg)' 
                            : hasUnread 
                              ? 'rgba(33, 150, 243, 0.04)' 
                              : 'var(--card-bg)',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '14px'
                        }}
                      >
                        {/* صورة الملف الشخصي مع مؤشر الاتصال */}
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                          <div className="profile-avatar" style={{ 
                            width: '52px', 
                            height: '52px', 
                            fontSize: '20px',
                            background: otherUser?.is_online 
                              ? 'linear-gradient(135deg, #00897B, #4CAF50)' 
                              : 'linear-gradient(135deg, #90a4ae, #b0bec5)'
                          }}>
                            {otherUser?.name?.charAt(0) || '؟'}
                          </div>
                          <OnlineIndicator isOnline={otherUser?.is_online} />
                        </div>

                        {/* معلومات المحادثة */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* السطر الأول: الاسم + الوقت */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: 1 }}>
                              <h4 style={{ 
                                margin: 0, 
                                fontSize: '15px', 
                                fontWeight: hasUnread ? '700' : '600',
                                color: 'var(--text-dark)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '140px'
                              }}>
                                {otherUser?.name || 'مستخدم'}
                              </h4>
                              {otherUser?.is_premium && <VerifiedBadge size="sm" />}
                            </div>
                            <span style={{ 
                              fontSize: '11px', 
                              color: hasUnread ? 'var(--primary)' : 'var(--text-light)',
                              fontWeight: hasUnread ? '600' : '400',
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}>
                              {getRelativeTime(lastMsgTime)}
                            </span>
                          </div>

                          {/* السطر الثاني: العمر + المدينة + الوظيفة */}
                          <p style={{ 
                            margin: '0 0 4px', 
                            fontSize: '12px', 
                            color: 'var(--text-light)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {otherUser?.age ? `${otherUser.age} سنة` : ''}
                            {otherUser?.city ? ` · ${otherUser.city}` : ''}
                            {otherUser?.job ? ` · ${otherUser.job}` : ''}
                          </p>

                          {/* السطر الثالث: آخر رسالة */}
                          {conv.last_message && (
                            <p style={{ 
                              margin: 0, 
                              fontSize: '13px', 
                              color: hasUnread ? 'var(--text-dark)' : 'var(--text-light)',
                              fontWeight: hasUnread ? '600' : '400',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {isMySent ? 'أنت: ' : ''}{conv.last_message.content}
                            </p>
                          )}
                        </div>

                        {/* أيقونات الحالة (ظرف + عداد) */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                          <MessageStatusIcon conv={conv} currentUserId={currentUser?.user_id} />
                          {hasUnread && (
                            <span data-testid="unread-badge" style={{
                              background: 'var(--primary)',
                              color: 'white',
                              borderRadius: '50%',
                              minWidth: '22px',
                              height: '22px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '11px',
                              fontWeight: '700'
                            }}>
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Chat Area */}
            {selectedConversation && (
              <div className="card" style={{ display: 'flex', flexDirection: 'column', height: '70vh', padding: '0', borderRadius: '16px', overflow: 'hidden' }}>
                {/* Chat Header */}
                <div style={{ 
                  padding: '14px 20px', 
                  borderBottom: '1px solid var(--border-color)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: 'var(--card-bg)'
                }}>
                  <div style={{ position: 'relative' }}>
                    <div className="profile-avatar" style={{ 
                      width: '45px', 
                      height: '45px', 
                      fontSize: '18px',
                      background: selectedConversation.other_user?.is_online 
                        ? 'linear-gradient(135deg, #00897B, #4CAF50)' 
                        : 'linear-gradient(135deg, #90a4ae, #b0bec5)'
                    }}>
                      {selectedConversation.other_user?.name?.charAt(0) || '؟'}
                    </div>
                    <OnlineIndicator isOnline={selectedConversation.other_user?.is_online} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span>{selectedConversation.other_user?.name}</span>
                      {selectedConversation.other_user?.is_premium && <VerifiedBadge size="sm" />}
                    </h3>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-light)' }}>
                      {selectedConversation.other_user?.is_online 
                        ? '🟢 متصل الآن' 
                        : selectedConversation.other_user?.last_seen 
                          ? `آخر ظهور ${getRelativeTime(selectedConversation.other_user.last_seen)}`
                          : selectedConversation.other_user?.job || ''
                      }
                      {selectedConversation.other_user?.city && !selectedConversation.other_user?.is_online 
                        ? ` · ${selectedConversation.other_user.city}` : ''}
                    </p>
                  </div>
                  <button 
                    data-testid="view-profile-btn"
                    className="btn btn-outline" 
                    style={{ marginRight: 'auto', padding: '8px 16px', fontSize: '13px' }}
                    onClick={() => navigate(`/profile/${selectedConversation.other_user?.user_id}`)}
                  >
                    عرض الملف
                  </button>
                </div>

                {/* Messages Area */}
                <div style={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  padding: '20px',
                  background: 'var(--light-bg)'
                }}>
                  {messages.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-light)' }}>
                      <p>ابدأ المحادثة!</p>
                    </div>
                  ) : (
                    messages.map((msg) => {
                      const isMyMsg = msg.from_user_id === currentUser?.user_id;
                      return (
                        <div
                          key={msg.message_id}
                          style={{
                            display: 'flex',
                            justifyContent: isMyMsg ? 'flex-start' : 'flex-end',
                            marginBottom: '12px'
                          }}
                        >
                          <div style={{
                            maxWidth: '70%',
                            padding: '12px 16px',
                            borderRadius: isMyMsg ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            background: isMyMsg 
                              ? 'linear-gradient(135deg, var(--primary), var(--purple))' 
                              : 'var(--card-bg)',
                            color: isMyMsg ? 'white' : 'var(--text-dark)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                          }}>
                            <p style={{ margin: 0, fontSize: '15px', lineHeight: '1.6' }}>{msg.content}</p>
                            <div style={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              gap: '6px',
                              marginTop: '6px'
                            }}>
                              <span style={{ fontSize: '11px', opacity: 0.7 }}>
                                {new Date(msg.created_at).toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {isMyMsg && (
                                <span data-testid={msg.read ? "msg-read-blue" : "msg-sent-gray"} style={{ 
                                  fontSize: '14px', 
                                  fontWeight: '700',
                                  color: msg.read 
                                    ? (isMyMsg ? '#81D4FA' : '#1E88E5')
                                    : (isMyMsg ? 'rgba(255,255,255,0.5)' : '#bdbdbd'),
                                  letterSpacing: '-2px'
                                }}>
                                  {msg.read ? '✓✓' : '✓'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div style={{ 
                  padding: '14px 20px', 
                  borderTop: '1px solid var(--border-color)',
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  background: 'var(--card-bg)'
                }}>
                  <input
                    data-testid="message-input"
                    type="text"
                    className="input"
                    placeholder="اكتب رسالتك..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    style={{ flex: 1, margin: 0 }}
                  />
                  <button 
                    data-testid="send-message-btn"
                    className="btn btn-primary"
                    onClick={sendMessage}
                    disabled={sendingMessage || !newMessage.trim()}
                    style={{ padding: '14px 24px' }}
                  >
                    {sendingMessage ? '...' : 'إرسال'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Received Requests Tab */}
        {!loading && activeTab === 'requests' && (
          <>
            {requests.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>📭</div>
                <h3 style={{ color: 'var(--text-dark)', marginBottom: '8px' }}>لا توجد طلبات</h3>
                <p style={{ color: 'var(--text-light)' }}>لم يتم استلام أي طلبات تواصل بعد</p>
              </div>
            ) : (
              <div className="grid grid-2">
                {requests.map((request) => (
                  <div key={request.request_id} className="card">
                    <div style={{ marginBottom: '16px' }}>
                      <div className="profile-avatar" style={{ width: '60px', height: '60px', fontSize: '24px', marginBottom: '12px' }}>
                        ؟
                      </div>
                      <h3 style={{ fontSize: '20px', marginBottom: '4px' }}>طلب تواصل جديد</h3>
                      <p style={{ fontSize: '14px', color: 'var(--text-light)' }}>
                        من المستخدم: {request.from_user_id}
                      </p>
                      <p style={{ fontSize: '13px', color: 'var(--text-light)', marginTop: '8px' }}>
                        {new Date(request.created_at).toLocaleDateString('ar')}
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button 
                        data-testid={`accept-request-${request.request_id}`}
                        className="btn btn-primary" 
                        style={{ flex: 1 }}
                        onClick={() => handleResponse(request.request_id, 'accepted')}
                      >
                        قبول
                      </button>
                      <button 
                        data-testid={`reject-request-${request.request_id}`}
                        className="btn btn-outline" 
                        style={{ flex: 1, borderColor: 'var(--error)', color: 'var(--error)' }}
                        onClick={() => handleResponse(request.request_id, 'rejected')}
                      >
                        رفض
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Sent Requests Tab */}
        {!loading && activeTab === 'sent' && (
          <>
            {sentRequests.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>📤</div>
                <h3 style={{ color: 'var(--text-dark)', marginBottom: '8px' }}>لم ترسل أي طلبات</h3>
                <p style={{ color: 'var(--text-light)' }}>ابدأ بالبحث وأرسل طلبات تواصل</p>
                <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={() => navigate('/search')}>
                  ابدأ البحث
                </button>
              </div>
            ) : (
              <div className="grid grid-2">
                {sentRequests.map((request) => (
                  <div key={request.request_id} className="card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                      <div className="profile-avatar" style={{ width: '60px', height: '60px', fontSize: '24px' }}>
                        {request.to_user?.name?.charAt(0) || '؟'}
                      </div>
                      <div>
                        <h3 style={{ fontSize: '18px', marginBottom: '4px' }}>
                          {request.to_user?.name || 'مستخدم'}
                        </h3>
                        <p style={{ fontSize: '14px', color: 'var(--text-light)' }}>
                          {request.to_user?.city} · {request.to_user?.age} سنة
                        </p>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '600',
                        background: request.status === 'accepted' ? 'rgba(0, 217, 165, 0.1)' :
                                   request.status === 'rejected' ? 'rgba(255, 87, 87, 0.1)' : 'rgba(255, 107, 157, 0.1)',
                        color: request.status === 'accepted' ? 'var(--success)' :
                               request.status === 'rejected' ? 'var(--error)' : 'var(--primary)'
                      }}>
                        {request.status === 'pending' ? 'قيد الانتظار' :
                         request.status === 'accepted' ? 'مقبول' : 'مرفوض'}
                      </span>
                      <span style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                        {new Date(request.created_at).toLocaleDateString('ar')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Upgrade Modal - يظهر عند تجاوز حد الرسائل المجانية */}
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="انتهت رسالتك المجانية 💌"
        message={upgradeModalMsg || 'لديك رسالة واحدة مجانية لكل محادثة. اشترك في Premium للاستمتاع برسائل غير محدودة!'}
      />
    </div>
  );
}

export default MessagesPage;
